import { Dialog, notifyError, notifySuccess } from '@/components'
import UnitSelect from '@/modules/add-new-unit/components/UnitSelect/UnitSelect'
import {
  useAddNewUnitMutation,
  useGetNewUnitQuery,
  useAddNewSmartTurnUnitMutation,
} from '@/modules/add-new-unit/services/newUnitService'
import { AddNewUnitRes } from '@/modules/add-new-unit/types/newUnitType'
import { UnitSearchReq } from '@/modules/devices-list/types/newUnitsType'
import { useGetUnitsHomeMapMutation } from '@/modules/maps/services/homeMapService'
import { useGetUnitsTypesListQuery } from '@/services/unitsListService'
import { timeout } from '@/utils'
import cn from '@/utils/cn'
import { useChangeSet } from '@/utils/useChangeSet'
import { yupResolver } from '@hookform/resolvers/yup'
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'
import { LoadingButton } from '@mui/lab'
import { Box, Grid, IconButton, Stack, Typography } from '@mui/material'
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import * as Yup from 'yup'
import Input from '../Input/Input'
import { SmartDevicesIds } from '@/types/enums'
import MapNew from '@/components/Organisms/Map/Map'
import SystemUploadImg from '@/components/Organisms/SystemUploadImg/SystemUploadImg'
import FormAddHeader from '@/components/Molecules/FormAddHeader/FormAddHeader'
import { sxWrapper } from '@/pages/Components/AddPagesWrapper'

const s = cn('add-new-unit')

interface IUnitData {
  setLoader: Dispatch<SetStateAction<boolean>>
}

const center = [47.2313, 39.7233]

const pointsIfEmpty = [
  [48.024402067130715, 39.85466330972504],
  [46.780699672601415, 39.807971415195674],
]

const defaultPoint = [47.06587193746529, 39.435380396518724]

export interface IUnitsFields {
  name: string
  address: string
  systemId: number | ''
  powerTypeId: number
  smartTurnTypeId: number
}

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required('Имя не может быть пустым')
    .trim('Имя не может быть пустым')
    .max(30, 'Поле должно содержать не более 30 символов'),
  address: Yup.string()
    .required('Адрес не может быть пустым')
    .trim('Адрес не может быть пустым')
    .max(250, 'Поле должно содержать не более 250 символов'),
  systemId: Yup.number()
    .required('Выберите систему')
    .typeError('Выберите систему'),
  powerTypeId: Yup.number().when('systemId', (systemId, schema) =>
    systemId === 1 || systemId === 2
      ? schema
          .required('Выберите систему питания')
          .typeError('Выберите систему питания')
      : schema
  ),
  smartTurnTypeId: Yup.number().when('systemId', (systemId, schema) =>
    systemId === 1
      ? schema
          .required('Выберите тип поворота')
          .typeError('Выберите тип поворота')
      : schema
  ),
})

const UnitData: FC<IUnitData> = ({ setLoader }) => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data } = useGetNewUnitQuery({ id: Number(id) })
  const { data: unitsTypesData, isLoading: isLoadingUnitsTypesList } =
    useGetUnitsTypesListQuery({ isNew: true, isAll: true, isSmart: true })
  const [addUnitService] = useAddNewUnitMutation()
  const [addSmartTurnUnitService] = useAddNewSmartTurnUnitMutation()
  const [getDataUnits, { data: locations }] = useGetUnitsHomeMapMutation()
  const [imageId, setImageId] = useState<string | null>(null)
  const [favoriteCategoryIds, setFavoriteCategoryIds] = useState<number[]>([])

  const [points, setPoints] = useState<number[][]>([])

  const onGetUnits = async () => {
    try {
      const usersSearchData: Omit<UnitSearchReq, 'page'> = {
        value: '',
        deviceTypeFilter: [0],
        deviceStateFilter: [0],
        favoriteId: 0,
        lightingTypeId: 0,
      }
      const res = await getDataUnits(usersSearchData).unwrap()
      if (res.success) {
        const units =
          res.data.data.length === 0
            ? pointsIfEmpty
            : res.data.data.map((unit) => unit.coordinates)
        setPoints(() => units)
      }
    } catch (e) {
      console.error(e)
    }
  }

  useEffect(() => {
    onGetUnits().then()
  }, [])

  const devicesList = unitsTypesData?.data.filter((device) =>
    SmartDevicesIds.includes(device.id)
  )

  const [openDialogSave, setOpenDialogSave] = useState(false)
  const [newCoords, setNewCoords] = useState<number[]>(defaultPoint)
  const refMark = useRef<any>(null)

  const defaultValues: IUnitsFields = {
    name: '',
    address: '',
    systemId: '',
    powerTypeId: 1,
    smartTurnTypeId: 1,
  }

  const {
    handleSubmit,
    control,
    reset,
    trigger,
    formState,
    watch,
    getValues,
    setValue,
  } = useForm<IUnitsFields>({
    defaultValues,
    resolver: yupResolver(validationSchema),
    criteriaMode: 'all',
    reValidateMode: 'onChange',
  })

  useEffect(() => {
    reset({
      name: data?.data?.name,
      address: '',
      systemId: '',
      powerTypeId: 1,
      smartTurnTypeId: 1,
    })
  }, [id, data])

  const onSaveBtnClick = () => {
    trigger().then(() => {
      const systemId = getValues('systemId')
      if (
        !getValues('name') ||
        !getValues('address') ||
        !getValues('systemId')
      ) {
        return
      }
      if (systemId === 1 || systemId === 2) {
        if (!getValues('powerTypeId')) return
        if (systemId === 1 && !getValues('smartTurnTypeId')) return
      }
      if (
        !formState.errors.name &&
        !formState.errors.address &&
        !formState.errors.systemId &&
        !formState.errors.powerTypeId &&
        !formState.errors.smartTurnTypeId
      ) {
        setOpenDialogSave(true)
      }
    })
  }

  const onSubmit = async (dataForm: IUnitsFields) => {
    try {
      setOpenDialogSave(false)
      setLoader(true)
      const newUnit: AddNewUnitRes = {
        id: Number(id),
        name: dataForm.name,
        address: dataForm.address,
        typeId: dataForm.systemId,
        powerTypeId: dataForm.powerTypeId,
        ...(dataForm.systemId === 1 && {
          smartTurnTypeId: dataForm.smartTurnTypeId,
        }),
        coordinates: JSON.stringify(newCoords),
        imei: data?.data?.imei ?? '',
        imageId: imageId ?? '',
        favoriteCategoryIds,
      }
      const res =
        dataForm.systemId === 1
          ? await addSmartTurnUnitService(newUnit).unwrap()
          : await addUnitService(newUnit).unwrap()
      if (res.success) {
        notifySuccess('Новое устройство успешно добавлено!')
        await timeout(1000)
        navigate('/new-units')
      }
    } catch (e) {
    } finally {
      setLoader(false)
    }
  }

  useChangeSet(() => {
    if (watch('name').length) {
      trigger('name')
    }
    if (watch('address').length) {
      trigger('address')
    }
    if (watch('systemId')) {
      trigger('systemId')
    }
    if (watch('powerTypeId')) {
      trigger('powerTypeId')
    }
    if (watch('smartTurnTypeId')) {
      trigger('smartTurnTypeId')
    }
  }, [
    watch('name'),
    watch('address'),
    watch('systemId'),
    watch('powerTypeId'),
    watch('smartTurnTypeId'),
  ])

  useChangeSet(() => {
    setValue('name', `${watch('name')} `, { shouldDirty: true })
  }, [newCoords])
  const handleChangeCoords = (coords: number[]) => {
    setNewCoords(coords)
  }
  return (
    <>
      <Dialog
        message='Изменения будут сохранены, продолжить?'
        open={openDialogSave}
        handleClose={() => setOpenDialogSave(false)}
        handleAgree={() => {
          if (Object.keys(formState.errors).length >= 1) {
            notifyError('Исправте ошибки в форме')
          }
          handleSubmit(onSubmit)()
        }}
      />
      <Box sx={sxWrapper}>
        <FormAddHeader
          title={`Устройство ${data?.data?.name}`}
          onFavoriteListChange={setFavoriteCategoryIds}
        />
        <Box
          sx={{
            display: 'flex',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: '10px',
              flexDirection: 'column',
              marginBottom: '10px',
              marginTop: '20px',
              marginLeft: '20px',
              width: '30%',
            }}
          >
            {data?.data?.lastConnectionDate ? (
              <Typography variant='body1'>
                подключено{' '}
                <span style={{ fontWeight: 'bold' }}>
                  {data?.data?.lastConnectionDate}
                </span>
              </Typography>
            ) : null}
            {data?.data?.ip ? (
              <Typography variant='body1'>
                {' '}
                с IP адреса{' '}
                <span style={{ fontWeight: 'bold' }}>{data?.data?.ip}</span>
              </Typography>
            ) : null}
            {data?.data?.imei ? (
              <Typography variant='body1'>IMEI: {data?.data?.imei}</Typography>
            ) : null}
            {data?.data?.iccid ? (
              <Typography variant='body1'>
                ICCID: {data?.data?.iccid}
              </Typography>
            ) : null}
          </Box>
          <Box
            className={'wrapper'}
            sx={{ width: '70%' }}
          >
            <Box
              className={'left'}
              style={{ width: '100%' }}
            >
              <Box className={'row'}>
                <Input
                  name='name'
                  type='text'
                  label='Наименование системы'
                  control={control}
                  //disabled={isLoadingUpdateOrganization}
                />
                <UnitSelect
                  disabled={isLoadingUnitsTypesList}
                  items={devicesList}
                  name='systemId'
                  label='Выберите систему'
                  control={control}
                />
                {watch('systemId') === 2 && (
                  <div
                    style={{
                      width: '450px',
                    }}
                  >
                    <UnitSelect
                      items={[
                        { id: 1, name: '12В' },
                        { id: 2, name: '24В' },
                        { id: 3, name: '220B' },
                      ]}
                      name='powerTypeId'
                      label='Выберите систему питания'
                      control={control}
                    />
                  </div>
                )}
              </Box>
              {watch('systemId') === 1 && (
                <Box className={'row'}>
                  <UnitSelect
                    items={[
                      { id: 1, name: '12В' },
                      { id: 2, name: '24В' },
                      { id: 3, name: '220B' },
                    ]}
                    name='powerTypeId'
                    label='Выберите систему питания'
                    control={control}
                  />
                  <UnitSelect
                    items={[
                      { id: 1, name: 'Односторонний' },
                      { id: 2, name: 'Двусторонний' },
                    ]}
                    name='smartTurnTypeId'
                    label='Выберите тип поворота'
                    control={control}
                  />
                </Box>
              )}
              <Box className={'row'}>
                <Input
                  multiline
                  minRows={2}
                  maxRows={5}
                  name='address'
                  type='text'
                  label='Адрес установки'
                  control={control}
                  //disabled={isLoadingUpdateOrganization}
                />
              </Box>
            </Box>
          </Box>
        </Box>
        <Box className={'mapContainer'}>
          <MapNew
            center={center}
            dataUnits={locations}
            setNewCoords={handleChangeCoords}
            newCoords={newCoords}
            draggable={true}
          />
        </Box>
        <Box className={'footerButtons'}>
          <SystemUploadImg
            imageId={imageId}
            onImageUploaded={setImageId}
          />
          <LoadingButton
            loading={false}
            variant='outlined'
            onClick={onSaveBtnClick}
            disabled={
              watch('name')?.trim()?.length === 0 ||
              watch('address')?.trim()?.length === 0 ||
              watch('systemId') === '' ||
              newCoords === defaultPoint ||
              (watch('systemId') === 1 &&
                (!watch('powerTypeId') || !watch('smartTurnTypeId'))) ||
              (watch('systemId') === 2 && !watch('powerTypeId'))
            }
          >
            <span>Сохранить</span>
          </LoadingButton>
        </Box>
      </Box>
    </>
  )
}

export default UnitData
